/**
 * Voice Ordering Component
 * Speech-to-order functionality for faster order entry
 * 
 * Flow:
 * 1. Exact match → Ask for quantity via voice → Add directly to cart
 * 2. Partial match → Show as suggestions for user to confirm
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Fab,
  Zoom,
  TextField
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { parseVoiceAPI } from '../../API/posAiAPI';
import './VoiceOrdering.css';

// Check for browser support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const isSpeechSupported = !!SpeechRecognition;

// Match types
const MATCH_TYPE = {
  EXACT: 'exact',      // 100% match - add directly after qty
  CLOSE: 'close',      // 80-99% match - show as suggestion
  PARTIAL: 'partial'   // 50-79% match - show as suggestion
};

const VoiceOrdering = ({ onAddItems, disabled = false }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFab, setShowFab] = useState(true);
  
  // Items categorized by match type
  const [exactMatches, setExactMatches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [addedItems, setAddedItems] = useState([]); // Items already added to cart
  
  // Quantity input mode for exact matches
  const [qtyInputMode, setQtyInputMode] = useState(false);
  const [currentExactItem, setCurrentExactItem] = useState(null);
  const [qtyInput, setQtyInput] = useState('');
  const [waitingForQty, setWaitingForQty] = useState(false);
  
  // Draggable FAB position
  const [fabPosition, setFabPosition] = useState({ x: 20, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const fabStartPos = useRef({ x: 0, y: 0 });
  
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const manualStop = useRef(false);
  const transcriptRef = useRef('');
  const addedItemsRef = useRef([]);
  
  // Keep refs in sync
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);
  
  useEffect(() => {
    addedItemsRef.current = addedItems;
  }, [addedItems]);
  
  // Confirm and add all items to cart (can be called by voice command)
  const confirmAndAddToCart = useCallback(() => {
    const allItems = [...addedItemsRef.current];
    console.log('🛒 Voice confirm - Adding items:', allItems.map(i => `${i.quantity}x ${i.productName}`));
    
    if (allItems.length > 0 && onAddItems) {
      onAddItems(allItems);
    }
    
    // Close dialog
    manualStop.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    recognitionRef.current?.stop();
    setDialogOpen(false);
    setTranscript('');
    setInterimTranscript('');
    setExactMatches([]);
    setSuggestions([]);
    setAddedItems([]);
    setError(null);
    setQtyInputMode(false);
    setCurrentExactItem(null);
    setWaitingForQty(false);
    setIsListening(false);
  }, [onAddItems]);

  // Process current transcript (called after silence)
  const processCurrentTranscript = useCallback(async () => {
    const currentTranscript = transcriptRef.current.trim();
    if (!currentTranscript || loading) return;
    
    console.log('📝 Processing transcript:', currentTranscript);
    setLoading(true);
    setError(null);

    try {
      const response = await parseVoiceAPI({ transcript: currentTranscript });
      
      // Handle offline/error responses gracefully
      if (response.offline) {
        console.log('📴 Voice parsing offline - voice ordering requires network');
        setError('Voice ordering requires network connection');
        setTranscript('');
        return;
      }
      
      if (response.success && response.data?.items?.length > 0) {
        const items = response.data.items;
        
        const exactNeedQty = [];
        const partial = [];
        const directAdd = [];
        
        items.forEach(item => {
          const confidence = item.matchConfidence || item.confidence || 0.7;
          const hasSpokenQty = item.hasSpokenQuantity === true;
          const itemWithQty = {
            ...item,
            quantity: item.quantity || 1,
            matchType: confidence >= 0.85 ? MATCH_TYPE.EXACT : 
                       confidence >= 0.7 ? MATCH_TYPE.CLOSE : MATCH_TYPE.PARTIAL
          };
          
          if (confidence >= 0.85) {
            if (hasSpokenQty && item.quantity > 0) {
              directAdd.push(itemWithQty);
            } else {
              exactNeedQty.push(itemWithQty);
            }
          } else if (confidence >= 0.5) {
            partial.push(itemWithQty);
          }
        });
        
        if (directAdd.length > 0) {
          setAddedItems(prev => [...prev, ...directAdd]);
          console.log(`✅ Auto-added ${directAdd.length} item(s)`);
        }
        
        setExactMatches(prev => [...prev, ...exactNeedQty]);
        setSuggestions(prev => [...prev, ...partial]);
        
        // If exact match needs quantity, show qty input mode
        if (exactNeedQty.length > 0 && !qtyInputMode) {
          setCurrentExactItem(exactNeedQty[0]);
          setQtyInputMode(true);
          setWaitingForQty(true);
          setQtyInput('');
        }
      } else if (!response.success && response.message) {
        // Show error message from API
        setError(response.message);
      }
      
      // Clear transcript for next input
      setTranscript('');
      
    } catch (err) {
      console.error('Failed to parse voice:', err);
      setError('Voice parsing failed');
    } finally {
      setLoading(false);
    }
  }, [loading, qtyInputMode]);

  // Initialize speech recognition
  useEffect(() => {
    if (!isSpeechSupported) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      console.log('🎤 Voice recognition started');
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript + ' ';
        } else {
          interim += result[0].transcript;
        }
      }

      if (final) {
        const finalLower = final.toLowerCase().trim();
        
        // Check for "add to cart" or "confirm" voice commands
        const addToCartCommands = [
          'add to cart', 'add to the cart', 'add all to cart',
          'all items add to cart', 'items add to cart',
          'confirm', 'confirm order', 'done', 'that\'s all',
          'finish', 'complete', 'checkout', 'proceed'
        ];
        
        const isAddToCartCommand = addToCartCommands.some(cmd => finalLower.includes(cmd));
        
        if (isAddToCartCommand && addedItemsRef.current.length > 0) {
          console.log('🛒 Voice command: Add to cart!');
          // Trigger confirm action
          confirmAndAddToCart();
          return;
        }
        
        // If waiting for quantity, parse the number
        if (waitingForQty && currentExactItem) {
          const qty = parseQuantityFromVoice(final);
          if (qty > 0) {
            addExactItemToCart(currentExactItem, qty);
            setWaitingForQty(false);
            setCurrentExactItem(null);
            setQtyInputMode(false);
            setTranscript('');
          } else {
            setTranscript(prev => prev + final);
          }
        } else {
          setTranscript(prev => prev + final);
        }
        
        // Reset timeout - process after 2 seconds of silence (but don't stop mic)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          // Process the transcript but keep listening
          // Use ref to get current value (avoid stale closure)
          if (transcriptRef.current.trim()) {
            processCurrentTranscript();
          }
        }, 2000);
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        setError('Microphone access denied. Please allow microphone access.');
        setIsListening(false);
      } else if (event.error === 'no-speech') {
        // Don't show error for no-speech, auto-restart
      } else if (event.error === 'aborted') {
        // Aborted by user, don't restart
        setIsListening(false);
      } else {
        // For other errors, try to restart if dialog is open
        console.log('Recognition error, will auto-restart if dialog open');
      }
    };

    recognition.onend = () => {
      console.log('🎤 Voice recognition ended');
      setIsListening(false);
      
      // Auto-restart if dialog is still open (continuous listening)
      if (dialogOpen && !manualStop.current) {
        setTimeout(() => {
          try {
            recognition.start();
            console.log('🎤 Auto-restarting recognition...');
          } catch (err) {
            console.log('Could not auto-restart:', err.message);
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      manualStop.current = true;
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [waitingForQty, currentExactItem, dialogOpen, processCurrentTranscript]);

  // Parse quantity from voice input
  const parseQuantityFromVoice = (text) => {
    const normalized = text.toLowerCase().trim();
    console.log('🔢 Parsing quantity from:', normalized);
    
    // Remove common suffixes that indicate quantity (qty, pcs, pc, pieces, etc.)
    const cleanedText = normalized
      .replace(/\s*(qty|quantity|pcs|pc|pieces|piece|nos|numbers?|items?)\s*/gi, ' ')
      .trim();
    
    console.log('🔢 Cleaned text:', cleanedText);
    
    // First check for digits (most reliable)
    const digitMatch = cleanedText.match(/\d+/);
    if (digitMatch) {
      const qty = parseInt(digitMatch[0], 10);
      console.log('🔢 Found digit quantity:', qty);
      return qty;
    }
    
    // Also check original text for digits
    const originalDigitMatch = normalized.match(/\d+/);
    if (originalDigitMatch) {
      const qty = parseInt(originalDigitMatch[0], 10);
      console.log('🔢 Found digit quantity (original):', qty);
      return qty;
    }
    
    // Number words mapping (comprehensive)
    const numberWords = {
      'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
      'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
      'eleven': 11, 'twelve': 12, 'thirteen': 13, 'fourteen': 14, 'fifteen': 15,
      'sixteen': 16, 'seventeen': 17, 'eighteen': 18, 'nineteen': 19,
      'twenty': 20, 'thirty': 30, 'forty': 40, 'fifty': 50,
      'sixty': 60, 'seventy': 70, 'eighty': 80, 'ninety': 90,
      'hundred': 100
    };
    
    // Check for compound numbers like "twenty one" = 21
    let total = 0;
    const words = cleanedText.split(/\s+/);
    
    for (const word of words) {
      if (numberWords[word]) {
        total += numberWords[word];
      }
    }
    
    if (total > 0) {
      console.log('🔢 Found word quantity:', total);
      return total;
    }
    
    // Single word check in original text
    for (const [word, num] of Object.entries(numberWords)) {
      if (normalized.includes(word)) {
        console.log('🔢 Found word quantity (partial):', num);
        return num;
      }
    }
    
    console.log('🔢 No quantity found');
    return 0;
  };

  // Add exact match item to cart
  const addExactItemToCart = (item, qty) => {
    const itemWithQty = { ...item, quantity: qty };
    setAddedItems(prev => [...prev, itemWithQty]);
    setExactMatches(prev => prev.filter(i => i._id !== item._id));
    
    // Show feedback
    console.log(`✅ Added ${qty}x ${item.productName} to cart`);
  };

  const startListening = useCallback(() => {
    if (!isSpeechSupported) {
      setError('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    manualStop.current = false; // Allow auto-restart
    setTranscript('');
    setInterimTranscript('');
    setExactMatches([]);
    setSuggestions([]);
    setAddedItems([]);
    setError(null);
    setQtyInputMode(false);
    setCurrentExactItem(null);
    setWaitingForQty(false);
    setDialogOpen(true);

    try {
      recognitionRef.current?.start();
    } catch (err) {
      console.error('Failed to start recognition:', err);
      setError('Failed to start voice recognition');
    }
  }, []);

  const stopListening = useCallback(() => {
    manualStop.current = true; // Prevent auto-restart
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    recognitionRef.current?.stop();
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  // Ask for quantity via voice
  const askForQuantity = (item) => {
    setCurrentExactItem(item);
    setQtyInputMode(true);
    setWaitingForQty(true);
    setQtyInput('');
    
    // Restart listening for quantity
    try {
      if (!isListening) {
        recognitionRef.current?.start();
      }
    } catch (err) {
      console.log('Recognition already running');
    }
  };

  // Handle manual quantity input
  const handleManualQtySubmit = () => {
    const qty = parseInt(qtyInput, 10);
    if (qty > 0 && currentExactItem) {
      addExactItemToCart(currentExactItem, qty);
      setQtyInputMode(false);
      setCurrentExactItem(null);
      setWaitingForQty(false);
      
      // Check if there are more exact matches
      if (exactMatches.length > 1) {
        const nextItem = exactMatches.find(i => i._id !== currentExactItem._id);
        if (nextItem) {
          setTimeout(() => askForQuantity(nextItem), 500);
        }
      }
    }
  };

  // Skip quantity input and use default
  const handleSkipQty = () => {
    if (currentExactItem) {
      addExactItemToCart(currentExactItem, 1);
      setQtyInputMode(false);
      setCurrentExactItem(null);
      setWaitingForQty(false);
    }
  };

  // Add suggestion to cart
  const handleAddSuggestion = (item) => {
    setAddedItems(prev => [...prev, item]);
    setSuggestions(prev => prev.filter(i => i._id !== item._id));
  };

  // Update suggestion quantity
  const handleSuggestionQtyChange = (index, delta) => {
    setSuggestions(prev => {
      const updated = [...prev];
      updated[index].quantity = Math.max(1, (updated[index].quantity || 1) + delta);
      return updated;
    });
  };

  // Remove suggestion
  const handleRemoveSuggestion = (index) => {
    setSuggestions(prev => prev.filter((_, i) => i !== index));
  };

  // Remove added item
  const handleRemoveAdded = (index) => {
    setAddedItems(prev => prev.filter((_, i) => i !== index));
  };

  // Confirm and add all items to cart
  const handleConfirm = () => {
    // Add all items from addedItems list (these are confirmed)
    const allItems = [...addedItems];
    console.log('🛒 Confirming items:', allItems.map(i => `${i.quantity}x ${i.productName}`));
    
    if (allItems.length > 0 && onAddItems) {
      onAddItems(allItems);
    }
    handleClose();
  };

  // Add all suggestions
  const handleAddAllSuggestions = () => {
    setAddedItems(prev => [...prev, ...suggestions]);
    setSuggestions([]);
  };

  const handleClose = () => {
    stopListening();
    setDialogOpen(false);
    setTranscript('');
    setInterimTranscript('');
    setExactMatches([]);
    setSuggestions([]);
    setAddedItems([]);
    setError(null);
    setQtyInputMode(false);
    setCurrentExactItem(null);
    setWaitingForQty(false);
  };

  const handleRetry = () => {
    setTranscript('');
    setInterimTranscript('');
    setExactMatches([]);
    setSuggestions([]);
    setError(null);
    setQtyInputMode(false);
    setCurrentExactItem(null);
    setWaitingForQty(false);
    
    try {
      recognitionRef.current?.start();
    } catch (err) {
      console.log('Restarting recognition');
    }
  };

  // Drag handlers for FAB
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    dragStartPos.current = { x: clientX, y: clientY };
    fabStartPos.current = { ...fabPosition };
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - dragStartPos.current.x;
    const deltaY = clientY - dragStartPos.current.y;
    
    // Calculate new position (from right and bottom)
    const newX = Math.max(10, Math.min(window.innerWidth - 70, fabStartPos.current.x - deltaX));
    const newY = Math.max(10, Math.min(window.innerHeight - 70, fabStartPos.current.y - deltaY));
    
    setFabPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Handle click vs drag
  const handleFabClick = (e) => {
    // Only trigger click if not dragging (moved less than 5px)
    const clientX = e.clientX || (e.changedTouches && e.changedTouches[0].clientX);
    const clientY = e.clientY || (e.changedTouches && e.changedTouches[0].clientY);
    const deltaX = Math.abs(clientX - dragStartPos.current.x);
    const deltaY = Math.abs(clientY - dragStartPos.current.y);
    
    if (deltaX < 5 && deltaY < 5) {
      startListening();
    }
  };

  if (!isSpeechSupported) {
    return (
      <Tooltip title="Voice ordering not supported in this browser">
        <span>
          <Fab 
            size="small" 
            disabled 
            className="voice-fab disabled"
          >
            <MicOffIcon />
          </Fab>
        </span>
      </Tooltip>
    );
  }

  const totalItemsToAdd = addedItems.length + suggestions.length;

  return (
    <>
      {/* Floating Action Button - Draggable */}
      <Zoom in={showFab}>
        <Tooltip title={isDragging ? '' : 'Voice Order (Drag to move, Click to speak)'}>
          <Fab
            ref={dragRef}
            size="medium"
            color="primary"
            onMouseDown={handleDragStart}
            onMouseUp={handleFabClick}
            onTouchStart={handleDragStart}
            onTouchEnd={handleFabClick}
            disabled={disabled}
            className={`voice-fab ${isListening ? 'listening' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
              position: 'fixed',
              right: fabPosition.x,
              bottom: fabPosition.y,
              zIndex: 1000,
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none'
            }}
          >
            <MicIcon />
          </Fab>
        </Tooltip>
      </Zoom>

      {/* Voice Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className="voice-dialog"
      >
        <DialogTitle className="voice-dialog-title">
          <Box className="title-content">
            <MicIcon className="title-icon" />
            <Typography variant="h6">Voice Order</Typography>
            {addedItems.length > 0 && (
              <Chip 
                icon={<ShoppingCartIcon />}
                label={`${addedItems.length} added`}
                size="small"
                color="success"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="voice-dialog-content">
          {/* Quantity Input Mode */}
          {qtyInputMode && currentExactItem && (
            <Box className="qty-input-mode">
              <Box className="qty-prompt">
                <Typography variant="h6" color="primary">
                  "{currentExactItem.productName}"
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Say the quantity or type below
                </Typography>
              </Box>
              
              {isListening && (
                <Box className="listening-for-qty">
                  <GraphicEqIcon className="wave-icon-small" />
                  <Typography variant="caption" color="primary">
                    Listening for quantity... (say "two", "3", etc.)
                  </Typography>
                </Box>
              )}
              
              <Box className="qty-manual-input">
                <TextField
                  type="number"
                  value={qtyInput}
                  onChange={(e) => setQtyInput(e.target.value)}
                  placeholder="Enter qty"
                  size="small"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleManualQtySubmit()}
                  sx={{ width: 100 }}
                />
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={handleManualQtySubmit}
                  disabled={!qtyInput || parseInt(qtyInput) < 1}
                >
                  Add
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={handleSkipQty}
                >
                  Add 1
                </Button>
              </Box>
            </Box>
          )}

          {/* Listening Indicator */}
          {isListening && !qtyInputMode && (
            <Box className="listening-indicator">
              <Box className="sound-wave">
                <GraphicEqIcon className="wave-icon" />
              </Box>
              <Typography variant="body2" color="primary">
                Listening... Speak your order
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Example: "2 chicken wings and 1 mojito"
              </Typography>
            </Box>
          )}

          {/* Transcript Display */}
          {(transcript || interimTranscript) && !qtyInputMode && (
            <Box className="transcript-box">
              <Typography variant="caption" color="textSecondary">
                You said:
              </Typography>
              <Typography variant="body1" className="transcript-text">
                {transcript}
                <span className="interim">{interimTranscript}</span>
              </Typography>
            </Box>
          )}

          {/* Loading */}
          {loading && (
            <Box className="loading-box">
              <CircularProgress size={24} />
              <Typography variant="body2">Processing your order...</Typography>
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="warning" className="error-alert">
              {error}
            </Alert>
          )}

          {/* Added Items (Exact Matches) */}
          {addedItems.length > 0 && (
            <Box className="added-items-box">
              <Typography variant="subtitle2" className="items-title added">
                <CheckIcon fontSize="small" /> Added to Cart ({addedItems.length})
              </Typography>
              <List dense className="items-list added-list">
                {addedItems.map((item, index) => (
                  <ListItem key={index} className="item-row added-row">
                    <ListItemText
                      primary={`${item.quantity}x ${item.productName}`}
                      secondary={`₹${(item.salesPrice * item.quantity)?.toFixed(2) || '0.00'}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleRemoveAdded(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Suggestions (Partial Matches) */}
          {suggestions.length > 0 && (
            <Box className="suggestions-box">
              <Box className="suggestions-header">
                <Typography variant="subtitle2" className="items-title suggestions">
                  <HelpOutlineIcon fontSize="small" /> Suggestions ({suggestions.length})
                </Typography>
                <Button 
                  size="small" 
                  onClick={handleAddAllSuggestions}
                  startIcon={<AddIcon />}
                >
                  Add All
                </Button>
              </Box>
              <List dense className="items-list suggestions-list">
                {suggestions.map((item, index) => (
                  <ListItem key={index} className="item-row suggestion-row">
                    <ListItemText
                      primary={item.productName}
                      secondary={
                        <span>
                          ₹{item.salesPrice?.toFixed(2) || '0.00'}
                          <Chip 
                            label={item.matchType === MATCH_TYPE.CLOSE ? 'Close match' : 'Partial match'}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, height: 18, fontSize: 10 }}
                          />
                        </span>
                      }
                    />
                    <ListItemSecondaryAction className="item-actions">
                      <IconButton 
                        size="small" 
                        onClick={() => handleSuggestionQtyChange(index, -1)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Chip 
                        label={item.quantity} 
                        size="small" 
                        className="qty-chip"
                      />
                      <IconButton 
                        size="small" 
                        onClick={() => handleSuggestionQtyChange(index, 1)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleAddSuggestion(item)}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleRemoveSuggestion(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* No items after parsing */}
          {!loading && !isListening && transcript && 
           addedItems.length === 0 && suggestions.length === 0 && 
           exactMatches.length === 0 && !error && !qtyInputMode && (
            <Alert severity="info">
              No products matched. Try speaking product names more clearly.
            </Alert>
          )}
        </DialogContent>

        <DialogActions className="voice-dialog-actions">
          {isListening && !qtyInputMode ? (
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<StopIcon />}
              onClick={stopListening}
            >
              Stop
            </Button>
          ) : (
            <>
              <Button 
                variant="outlined" 
                startIcon={<MicIcon />}
                onClick={handleRetry}
              >
                {addedItems.length > 0 ? 'Add More' : 'Retry'}
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<ShoppingCartIcon />}
                onClick={handleConfirm}
                disabled={addedItems.length === 0}
              >
                Confirm ({addedItems.length} item{addedItems.length !== 1 ? 's' : ''})
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoiceOrdering;
